/**
 * AuthProvider — single source of truth for "who is logged in".
 *
 * Strategy:
 *   - On mount, GET /api/auth/me. If it succeeds, we're authenticated.
 *   - On 401, the api/client redirects to /login. AuthProvider treats
 *     the absence of `me` as "not authenticated" and renders nothing
 *     (the redirect is already in flight).
 *   - `login(creds)` POSTs /api/auth/login then refetches `me`.
 *   - `logout()` POSTs /api/auth/logout and clears state.
 *
 * State is intentionally kept in React Query so any component can call
 * `useAuth()` without prop-drilling. We don't persist anything in
 * localStorage — the session cookie does the work.
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { fetchJson, postJson } from "../api/client";
import { useNotificationSocket } from "../api/notifications";
import type { AuthMe, LoginInput } from "../api/types";

interface AuthContextValue {
  me: AuthMe | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (input: LoginInput) => Promise<AuthMe>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ME_KEY = ["auth", "me"] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      const body = await fetchJson<{ user: AuthMe }>("/api/auth/me");
      return body.user;
    },
    // Don't retry on 401 — the client already redirected.
    retry: false,
    // Keep in cache; refresh on remounts so login flips state immediately.
    staleTime: 30_000,
  });

  const loginMutation = useMutation({
    mutationFn: async (input: LoginInput) => {
      const body = await postJson<{ ok: true; user: AuthMe }>(
        "/api/auth/login",
        input,
      );
      return body.user;
    },
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => postJson<{ ok: true }>("/api/auth/logout", {}),
    onSettled: () => {
      queryClient.setQueryData(ME_KEY, null);
      queryClient.clear();
    },
  });

  useNotificationSocket(Boolean(meQuery.data));

  const value = useMemo<AuthContextValue>(
    () => ({
      me: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: Boolean(meQuery.data),
      login: async (input) => {
        const me = await loginMutation.mutateAsync(input);
        return me;
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ME_KEY });
      },
    }),
    [meQuery.data, meQuery.isLoading, loginMutation, logoutMutation, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
